import { FormButtonBox } from "@/features/standard/Form/FormButtonBox";
import { FormMain } from "@/features/standard/Form/FormMain";
import { FormWrapper } from "@/features/standard/Form/FormWrapper";

import { mutateInventories } from "@/api/inventories/useInventoriesQuery";
import { BarcodeInput } from "@/features/barcodeInput/BarcodeInput";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/alert";
import { InventoriesAutoComplete } from "@/features/ui/autoComplete/inventoryLot/inventory-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { MantineSelectableTable } from "@/features/ui/table/MantineSelectableTable";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useInventoriesFormFetch } from "@/hooks/useInventoriesFormFetch";
import { handleErrorResponse } from "@/utils/errorMessage";
import { koToEnKeyboard } from "@/utils/koToEnKeyboard";
import { Button, NumberInput, Select, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  AuthSignupPost201ResponseEquipmentWorksInner,
  ProductionActionApiWorksWorkIdInputPutRequest,
  ProductionPlansGet200ResponseRowsInnerWorksInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "mantine-react-table";
import { flushSync } from "react-dom";

const Create = Object.assign({}, FormMain, {
  Button: Button,
  TextInput: TextInput,
  Select: Select,
  Table: MantineSelectableTable,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  InventoriesAutoComplete: InventoriesAutoComplete,
  LocationAutoComplete: LocationAutoComplete,
  NumberInput: NumberInput,
  BarcodeInput: BarcodeInput,
});

type PageKeyType = "equipment" | "plan"; // pageKey의 가능한 값들을 정의합니다.

type ItemType<T extends PageKeyType> = T extends "equipment"
  ? AuthSignupPost201ResponseEquipmentWorksInner
  : ProductionPlansGet200ResponseRowsInnerWorksInner; // pageKey에 따른 item의 타입을 정의합니다.

interface WorkInputFormProps<T extends PageKeyType> {
  workRowData: ItemType<T>;
}

export function RawMaterialSelectLocationForm<T extends PageKeyType>(
  params: WorkInputFormProps<T>,
): JSX.Element {
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const actions = useWorkActions();

  const workData =
    "routingData" in params.workRowData
      ? (params.workRowData as ProductionPlansGet200ResponseRowsInnerWorksInner)
      : undefined;

  const targetLocationCode = workData?.locationSetting?.toLocation?.code;
  const targetLocationName = workData?.locationSetting?.toLocation?.name;

  const routingBoms = workData?.routingData?.routingBoms;

  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemCode: bom.itemCode,
    itemName: bom.item?.name,
    routingCode: bom.routingCode,
  }));

  const form = useForm({
    initialValues: {
      useBarcode: false,
      barcodeInput: "",
      itemCode: "",
      workId: workData?.id,
      worksWorkIdInputPutRequest: {
        lotId: "",
        sourceLocationCode: "",
        targetLocationCode: targetLocationCode,
        quantity: 0,
      },
    },
  });

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes: form.values.itemCode ?? undefined,
    locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
    lotName: form.values.barcodeInput,
  });

  const { mutate: workInputMutate } = useMutation(
    (params: ProductionActionApiWorksWorkIdInputPutRequest) =>
      mutateInventories.workInput(params).mutationFn(undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["inventories"]);
        queryClient.invalidateQueries(["workSlitting"]); // 원부자재 투입 후 슬리팅 규격 목록 갱신
        actions.setWorks((prevWorks) => []);
        closeModal(form.values);
        customAlert("원부자재 투입에 성공하였습니다.", "원부자재 투입 성공", "green");
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: "원부자재 투입" }),
          "원부자재 투입 실패",
          "red",
        );
      },
    },
  );

  // 순서 로트명 품목명 Grade Color 형태 필요 원자재량 투입량 Actions
  const columns = () =>
    [
      {
        accessorKey: "itemCode",
        header: "품목코드",
      },
      {
        accessorKey: "itemName",
        header: "품목명",
      },
      {
        accessorKey: "routingCode",
        header: "라우팅 코드",
      },
    ] as MRT_ColumnDef<any>[];

  const handleOnSubmit = () => {
    const worksWorkIdInputPutRequest = {
      lotId: Number(form.values.worksWorkIdInputPutRequest.lotId),
      sourceLocationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
      targetLocationCode: form.values.worksWorkIdInputPutRequest.targetLocationCode,
      quantity: String(form.values.worksWorkIdInputPutRequest.quantity),
    };
    workInputMutate({
      workId: workData?.id || 0,
      worksWorkIdInputPutRequest: worksWorkIdInputPutRequest,
    });
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
  };

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data?.data?.data;
    const noData = !inventoryData?.length;
    const lotId = inventoryData?.[0]?.lotId;

    if (noData) {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue("worksWorkIdInputPutRequest.lotId", "");
      form.setFieldValue("worksWorkIdInputPutRequest.quantity", 0);
      customAlert(
        "존재하지 않은 로트입니다.",
        "바코드에 해당하는 로트가 존재하지 않습니다.",
        "red",
      );
    } else {
      form.setFieldValue("barcodeInput", "");
      form.setFieldValue("worksWorkIdInputPutRequest.lotId", lotId.toString());
      form.setFieldValue("worksWorkIdInputPutRequest.quantity", Number(inventoryData[0].quantity));
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Unidentified") {
      // 바코드 리더기 또는 입력기 문제로 인한 불명확한 키는 무시
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      const normalized = normalizeBarcode(form.values.barcodeInput);
      flushSync(() => {
        form.setFieldValue("barcodeInput", normalized);
        form.setFieldValue("useBarcode", true);
      });
      refetchHandler();
    }
  };

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    flushSync(() => {
      form.setFieldValue("barcodeInput", normalizeBarcode(inputValue));
      form.setFieldValue("useBarcode", true);
    });
    refetchHandler();
  };

  const normalizeBarcode = (raw: string) => {
    // 필요 시 전체 대문자 정책 적용: .toUpperCase()
    return koToEnKeyboard(raw.trim());
  };

  return (
    <Create.Wrapper w={"100%"}>
      <Create.Table
        enableMultiRowSelection={false}
        columns={columns()}
        data={convertedRoutingBoms ?? []}
        onRowSelection={handleOnRowSelection}
      />

      <Create.LocationAutoComplete
        withAsterisk
        label={"로케이션"}
        {...form.getInputProps("worksWorkIdInputPutRequest.sourceLocationCode")}
      />

      <Create.BarcodeInput
        disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
        handleBlur={handleBlur}
        onKeyDown={handleKeyDown}
        {...form.getInputProps("barcodeInput")}
      />

      <Create.InventoriesAutoComplete
        disabled={!form.values.worksWorkIdInputPutRequest.sourceLocationCode}
        withAsterisk
        label={"로트 정보"}
        value={form.values.worksWorkIdInputPutRequest.lotId}
        query={
          form.values.itemCode
            ? {
                itemCode: form.values.itemCode,
                locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                quantity: { $gt: 0 },
              }
            : {
                itemCode: undefined,
                locationCode: form.values.worksWorkIdInputPutRequest.sourceLocationCode,
                quantity: { $gt: 0 },
              }
        }
        onChange={(value) => {
          const quantity = Number(
            data?.data?.filter((row) => row.lotId === Number(value))[0]?.quantity ?? 0,
          );
          form.setFieldValue("useBarcode", false);
          form.setFieldValue("worksWorkIdInputPutRequest.quantity", quantity);
          form.setFieldValue("worksWorkIdInputPutRequest.lotId", value ?? "");
        }}
      />
      <Create.TextInput
        withAsterisk
        disabled
        label={"입고 로케이션 코드"}
        value={targetLocationName}
      />
      <Create.NumberInput
        withAsterisk
        label={"수량"}
        {...form.getInputProps("worksWorkIdInputPutRequest.quantity")}
      />
      <Create.ButtonBox>
        <Create.Button color="gray" onClick={closeModal}>
          취소
        </Create.Button>
        <Create.Button onClick={handleOnSubmit}>투입</Create.Button>
      </Create.ButtonBox>
    </Create.Wrapper>
  );
}
